import React, { useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import ReactGA from "react-ga";

// Local component imports
import CourseContentContainer from "./courseContentDisplays/CourseContentContainer";
import CourseGameLayout from "./courseContentDisplays/CourseGameLayout";
import CoursePayment from "./CoursePayment";
import CourseSelectorScreen from "./gameSetupAndLobby/CourseSelectorScreen";
import DeleteMyData from "../pages/DeleteMyData";
import DevTestScreen from "../pages/DevTestScreen";
import CourseContentReview from "../pages/CourseContentReview";
import Feedback from "../pages/Feedback";
import GameOverview from "../pages/GameOverview";
import WhatToExpect from "../pages/WhatToExpect";
import HomePage from "../pages/HomePage";
import LeaderboardContainer from "./leaderboard/LeaderboardContainer";
import LeaderboardCourseSelector from "./leaderboard/LeaderboardCourseSelector";
import Map from "./common/Map";
import MixAndMatch from "./courseContentDisplays/MixAndMatch";
import TeamNameInGamePrompt from "./gameSetupAndLobby/TeamNameInGamePrompt";
import PageNotFound from "../pages/PageNotFound";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import RateUs from "../pages/RateUs";
import Rules from "../pages/Rules";
import ScoreSummary from "./leaderboard/ScoreSummary";
import TeamsJoinOrHost from "../pages/TeamsJoinOrHost";
import ProfileSetup from "../pages/ProfileSetup";
import TestCourseLanding from "../pages/TestCourseLanding";
import JoinOrHost from "./joinOrHost";
import TheEnd from "./leaderboard/TheEnd";
import Tipping from "../pages/Tipping";
import Welcome from "../pages/Welcome";

// Utility/helper imports
import { ExpoIntegration } from './expo/ExpoIntegration';
import { isProd } from "./helpers/misc";
import { updatePlatform } from '../redux/platformSlice';
import { usePlatformInsets } from './helpers/usePlatformInsets';
import * as route from "./helpers/routes";
import Lobby from "../pages/Lobby";

//import AirBnbWelcome from "./AirBnbWelcome";

const PlatformRedirect = () => {
  const { platform, statusBarHeight, bottomInset } = useParams();
  const dispatch = useDispatch();
  
  useEffect(() => {
    dispatch(updatePlatform({
      platform: platform || 'web',
      statusBarHeight: Number(statusBarHeight) || 0,
      bottomInset: Number(bottomInset) || 0,
    }));
  }, [platform, statusBarHeight, bottomInset, dispatch]);
  
  return <Navigate to="/" replace />;
};

const Routing = () => {
  const { uid } = useSelector((state) => state.user);
  const { teamDetails } = useSelector((state) => state.team);
  const courseName = teamDetails?.currentCourseSelection || "";

  useEffect(()=>{
    isProd && ReactGA.set({userId: uid });
  },[uid]);
  
  useEffect(()=>{
    isProd && ReactGA.set({course: courseName });
  },[courseName]);
  //google analytics trigger
  //useEffect to send the initial page load "/"
  useEffect(() => {
    isProd && ReactGA.pageview(window.location.pathname + window.location.search);
  }, );
  //listener that sends new pages to google analytics after initial load

  useEffect(()=>{
    if(!isProd){
      return;
    }
    window?.FS?.identify(uid, {});
  }, [uid]);

  // Track route changes for analytics
  useEffect(() => {
    if (isProd) {
      ReactGA.pageview(window.location.pathname + window.location.search);
    }
  }, [window.location.pathname, window.location.search]);

  const platform = useSelector((state) => state.platform);
  const { bottomInset } = usePlatformInsets();

  return (
    <div style={{
      paddingTop: platform.statusBarHeight,
      paddingBottom: bottomInset,
      minHeight: '100%',
      boxSizing: 'border-box',
    }}>
        <ExpoIntegration/>
        <Routes>
          <Route
            path="/platform/:platform/:statusBarHeight/:bottomInset"
            element={
              <PlatformRedirect />
            }
          />
          <Route
            path="/platform/:platform/:statusBarHeight"
            element={
              <PlatformRedirect />
            }
          />
          <Route path={route.GAMEOVERVIEW} element={<GameOverview/>}/>
          <Route path={route.WHAT_TO_EXPECT} element={<WhatToExpect/>}/>
          <Route path={route.SPECIALWELCOME} element={<GameOverview specialName={'John and Jane'} />} />
          <Route path={route.HOME} element={<HomePage/>}/>
          <Route path={`/course/:course${route.MAP}`} element={<Map/>}/>
          <Route path={`/course/:course${route.END}`} element={<TheEnd/>}/>
          <Route path={`/course/:course${route.SUMMARY}`} element={<ScoreSummary/>}/>
          <Route path={`/course/:course${route.PAY}`} element={<CoursePayment/>}/>
          <Route path="/course/:course/q/:question" element={<CourseGameLayout />}>
            <Route index element={<CourseContentContainer />} />
            <Route path="answer" element={<CourseContentContainer />} />
          </Route>
          <Route path={route.WELCOME} element={<Welcome/>}/>
          <Route path={route.COURSEVIEWER_MAIN} element={<CourseSelectorScreen courseViewType="casual"/>}/>
          <Route path={route.COURSESELECTOR_HOST} element={<CourseSelectorScreen courseViewType="host"/>}/>
          <Route path={route.COURSEVIEWER_LOBBY} element={<CourseSelectorScreen courseViewType="nonHost"/>}/>
          <Route path={route.LOBBY} element={<Lobby/>}/>
          <Route path={route.PROFILE_SETUP} element={<ProfileSetup />} />
          <Route path={route.FEEDBACK} element={<Feedback
            sourceURL={"https://docs.google.com/forms/d/e/1FAIpQLSfboRB0-yq0PwdqBLpTcfvrnsGc8Cg7VT0ltiYxZnAh7iDqBA/viewform?embedded=true"}/>}/>
          <Route path={route.POSTGAMESURVEY} element={<Feedback
            sourceURL={"https://docs.google.com/forms/d/e/1FAIpQLSe819yRlFOlYJv6AQLSirRxTVu_TL6iPcEWOk049D13B62w5A/viewform?embedded=true"}/>}/>
          <Route path={route.TIP} element={<Tipping/>}/>
          <Route path={route.TEAMNAME} element={<TeamNameInGamePrompt />} />
          <Route path={route.RULES} element={<Rules/>}/>
          <Route path={route.LEADERBOARD} element={<LeaderboardCourseSelector/>}/>
          <Route path={route.LEADERBOARD+'/:course'} element={<LeaderboardContainer/>}/>
          <Route path={route.RATEUS} element={<RateUs/>}/>
          <Route path={route.DELETEMYDATA} element={<DeleteMyData/>}/>
          <Route path={route.PRIVACYPOLICY} element={<PrivacyPolicy/>}/>
          <Route path={route.TEST} element={<DevTestScreen/>}/>
          <Route path={`${route.COURSE_CONTENT_REVIEW}/:course`} element={<CourseContentReview/>}/>
          <Route path={route.TESTLANDING} element={<TestCourseLanding/>}/>
          <Route path={route.TEAMS} element={<TeamsJoinOrHost/>}/>
          <Route path={route.JOINORHOST} element={<JoinOrHost/>}/>
          <Route path="/mm" element={<MixAndMatch/>}/>
          <Route path="/" element={<GameOverview/>}/>
          <Route path="*" element={<PageNotFound />} />
          {/*<Route exact path="/" component={AirBnbWelcome}/>*/}
        </Routes>
    </div>
  )
};

export default Routing;
